<template>
  <div ref="root">
    <UiTooltip tooltip-position="top" theme="material" :label="$t(name)">
      <div
        ref="trigger"
        @click="showDropdown"
        class="tw:relative tw:cursor-pointer tw:border tw:rounded-lg tw:flex tw:items-center tw:px-3 tw:py-2 tw:pr-8 box-size-44"
        :class="getFieldClass(show, disabled)"
      >
        <div class="tw:flex tw:justify-between tw:w-full">
          <span class="tw:truncate">{{ $t(name) }}</span>
          <div
            v-if="value.length > 0"
            class="tw:bg-blue-700 tw:rounded-full tw:w-[23px] tw:h-[23px] tw:flex tw:items-center tw:justify-center"
          >
            <span class="tw:text-white tw:text-sm tw:leadning-none">{{ value.length }}</span>
          </div>
        </div>

        <ChevronRight class="tw:absolute tw:size-6" :class="show ? 'arrow-dropdown-open' : 'arrow-dropdown'" />
      </div>
    </UiTooltip>
    <Teleport to="body">
      <div
        v-if="show && !disabled"
        ref="dropdown"
        class="tw:fixed tw:min-h-[50px] tw:bg-white tw:rounded dropdown-card tw:p-4 tw:z-[9999]"
        :style="dropdownStyle"
      >
        <p class="tw:font-bold tw:text-sm tw:mb-3">{{ tagline }}</p>
        <ul :class="{ 'tw:mb-3': name === 'competitors' }">
          <li
            v-for="(option, index) in options"
            :key="option.id"
            @click="onSelect({ option, name })"
            class="tw:flex tw:items-center tw:justify-between tw:mx-1 tw:py-1.5"
          >
            <label class="tw:mb-0" :for="index">{{ name === 'channels' ? option.fullName : option.name }}</label>
            <input type="checkbox" :id="index" :checked="isChecked(option)" :disabled="isDisabled(option)" />
          </li>
        </ul>

        <button
          @click="redirectToUpdateCompetitors"
          v-if="name === 'competitors'"
          class="tw:mx-1 tw:p-0 tw:whitespace-nowrap"
        >
          <span class="tw:font-bold tw:text-blue-700">{{ $t('manage_competitors') }}</span>
        </button>
        <button
          @click="redirectToUpdateChannels"
          v-if="name === 'channels'"
          class="tw:mx-1 tw:p-0 tw:whitespace-nowrap"
        >
          <span class="tw:font-bold tw:text-blue-700">{{ $t('manage_channels') }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>
<script setup>
import { ref, computed, nextTick, onMounted, onDeactivated } from 'vue'
import { useRouter } from 'vue-router'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'

const router = useRouter()
const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  name: {
    type: String,
    default: '',
  },
  value: {
    type: Array,
    default: () => [],
  },
  tagline: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const root = ref(null)
const trigger = ref(null)
const dropdown = ref(null)
const show = ref(false)
const dropdownPosition = ref({ top: 0, left: 0, minWidth: 0 })
const emit = defineEmits(['select'])

const dropdownStyle = computed(() => ({
  top: `${dropdownPosition.value.top}px`,
  left: `${dropdownPosition.value.left}px`,
  minWidth: `${dropdownPosition.value.minWidth}px`,
}))

const updateDropdownPosition = () => {
  if (!trigger.value) return
  const rect = trigger.value.getBoundingClientRect()
  dropdownPosition.value = {
    top: rect.bottom + 5,
    left: rect.left,
    minWidth: rect.width,
  }
}

const showDropdown = () => {
  show.value = !show.value
  if (show.value) {
    nextTick(updateDropdownPosition)
  }
}
const onSelect = ({ option, name }) => {
  emit('select', { option, name })
}
const isChecked = option => {
  return props.value.find(item => item.id === option.id)
}

const handleClickOutside = evt => {
  if (
    root?.value !== null &&
    !root?.value?.contains(evt.target) &&
    !dropdown?.value?.contains(evt.target)
  ) {
    show.value = false
  }
}

const isDisabled = option => {
  return props.value.length === 1 && isChecked(option)
}

const redirectToUpdateCompetitors = () => {
  router.push({ name: 'rate-insights-update', query: { isUpdate: true } })
}
const redirectToUpdateChannels = () => {
  router.push({ name: 'rate-insights-channels' })
}
const getFieldClass = (show, disabled) => {
  const classes = []
  if (disabled) {
    classes.push('tw:border-gray-300 tw:text-gray-300')
  }
  if (show) {
    classes.push('tw:border-blue-700')
  }
  if (!show && !disabled) {
    classes.push('tw:border-gray-500')
  }
  return classes.toString().replace(',', ' ')
}

const handleScroll = () => {
  if (show.value) {
    show.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('scroll', handleScroll, true)
})

onDeactivated(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleScroll, true)
})
</script>
<style scoped>
.arrow-dropdown {
  top: 50%;
  transform: translateY(-45%) rotate(90deg);
  right: 6px;
  pointer-events: none;
}
.arrow-dropdown-open {
  top: 50%;
  transform: translateY(-45%) rotate(270deg);
  right: 6px;
  pointer-events: none;
}

.dropdown-card {
  box-shadow:
    0 2px 4px 0 rgba(63, 87, 189, 0.1),
    0 2px 10px 0 rgba(108, 112, 122, 0.15);
}
</style>
